/**
 * PATH: apps/quote/src/pages/DashboardPage.tsx
 * PURPOSE: Quote Hub dashboard with real-time stats and recent quotes from API
 *
 * WHY:
 * - Provides at-a-glance overview of quoting activity
 * - Shows key metrics (active, pending, accepted, sent)
 * - Quick access to recent quotes and actions
 *
 * FLOW:
 *   ┌─────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Fetch Stats │───▶│ Compute KPI │───▶│ Render UI   │
 *   │ from API    │    │ from quotes │    │ with data   │
 *   └─────────────┘    └─────────────┘    └─────────────┘
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FileText,
  Clock,
  CheckCircle,
  Send,
  DollarSign,
  TrendingUp,
  Plus,
  ArrowUpRight,
  RefreshCw,
  Sparkles,
  BarChart3,
  Target,
  Zap,
  ChevronRight,
} from 'lucide-react'
import { cn, formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import { quoteApi, type QuoteListItem } from '@/lib/quoteApi'

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = Number.parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

export default function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ['quote-dashboard-stats'],
    queryFn: () => quoteApi.getDashboardStats(),
    refetchInterval: 60000, // Refresh every minute
  })

  const stats = statsQuery.data
  const isLoading = statsQuery.isLoading

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="p-6 lg:p-8 space-y-8 max-w-[1600px] mx-auto">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 p-8 text-white shadow-xl">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnptMCAzMGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnoiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <span className="text-blue-100 text-sm font-medium">Quote Hub</span>
              </div>
              <h1 className="text-3xl font-bold mb-2">Welcome back</h1>
              <p className="text-blue-100 max-w-md">
                {isLoading
                  ? 'Loading your quoting activity...'
                  : `You have ${stats?.activeQuotes || 0} active quotes worth ${formatCurrency(stats?.totalValueThisMonth || 0)} this month`}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => statsQuery.refetch()}
                disabled={statsQuery.isFetching}
                className="h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 flex items-center gap-2 transition-colors"
              >
                <RefreshCw className={cn('h-4 w-4', statsQuery.isFetching && 'animate-spin')} />
                Refresh
              </button>
              <Link
                to="/builder"
                className="h-10 px-5 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 flex items-center gap-2 transition-colors shadow-lg"
              >
                <Plus className="h-4 w-4" />
                New Quote
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid - Clickable Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/quotes?status=active" className="block">
            <StatCard
              title="Active Quotes"
              value={isLoading ? '—' : String(stats?.activeQuotes || 0)}
              change={stats?.activeQuotesChange || ''}
              icon={FileText}
              color="blue"
              isLoading={isLoading}
              clickable
            />
          </Link>
          <Link to="/quotes?status=pending" className="block">
            <StatCard
              title="Pending Review"
              value={isLoading ? '—' : String(stats?.pendingReview || 0)}
              change={stats?.pendingReviewChange || ''}
              icon={Clock}
              color="amber"
              isLoading={isLoading}
              clickable
            />
          </Link>
          <Link to="/quotes?status=accepted" className="block">
            <StatCard
              title="Accepted"
              value={isLoading ? '—' : String(stats?.acceptedTotal || 0)}
              change={stats?.acceptedChange || ''}
              icon={CheckCircle}
              color="emerald"
              isLoading={isLoading}
              clickable
            />
          </Link>
          <Link to="/quotes?status=sent" className="block">
            <StatCard
              title="Sent This Week"
              value={isLoading ? '—' : String(stats?.sentThisWeek || 0)}
              change={stats?.sentThisWeekValue ? formatCurrency(stats.sentThisWeekValue) + ' value' : ''}
              icon={Send}
              color="violet"
              isLoading={isLoading}
              clickable
            />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Quotes */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/60 dark:border-slate-800 overflow-hidden">
              <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <FileText className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Quotes</h2>
                    <p className="text-xs text-slate-500">Last 5 quotes</p>
                  </div>
                </div>
                <Link
                  to="/quotes"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 font-medium"
                >
                  View all <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="p-4 animate-pulse">
                      <div className="flex items-center gap-4">
                        <div className="h-11 w-11 rounded-xl bg-slate-200 dark:bg-slate-700" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
                          <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-1/3" />
                        </div>
                        <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
                      </div>
                    </div>
                  ))
                ) : stats?.recentQuotes?.length ? (
                  stats.recentQuotes.map((quote: QuoteListItem) => (
                    <Link
                      key={quote.id}
                      to={`/quotes/${quote.id}`}
                      className="group flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center border border-slate-200 dark:border-slate-700 group-hover:border-blue-200 dark:group-hover:border-blue-800 transition-colors">
                          <FileText className="h-5 w-5 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {quote.project_name || 'Untitled Project'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {quote.quote_number} • {quote.client_name || 'No client'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', getStatusColor(quote.status))}>
                          {getStatusLabel(quote.status)}
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-white w-24 text-right tabular-nums">
                          {formatCurrency(toNumber(quote.total_excl_gst))}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">No quotes yet</p>
                    <Link to="/builder" className="text-blue-600 text-sm font-medium mt-2 inline-block">
                      Create your first quote →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Monthly Stats */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/60 dark:border-slate-800 p-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                  <BarChart3 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">This Month</h3>
                  <p className="text-xs text-slate-500">Performance overview</p>
                </div>
              </div>

              <div className="space-y-4">
                <MetricRow
                  label="Quotes Created"
                  value={isLoading ? '—' : String(stats?.quotesThisMonth || 0)}
                  isLoading={isLoading}
                />
                <MetricRow
                  label="Total Value"
                  value={isLoading ? '—' : formatCurrency(stats?.totalValueThisMonth || 0)}
                  isLoading={isLoading}
                  highlight
                />
                <MetricRow
                  label="Win Rate"
                  value={isLoading ? '—' : `${stats?.winRate || 0}%`}
                  isLoading={isLoading}
                  color={stats?.winRate && stats.winRate >= 50 ? 'emerald' : 'amber'}
                />
                <MetricRow
                  label="Avg. Quote Value"
                  value={isLoading ? '—' : formatCurrency(stats?.avgQuoteValue || 0)}
                  isLoading={isLoading}
                />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/60 dark:border-slate-800 p-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-sm">
                  <Zap className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Quick Actions</h3>
                  <p className="text-xs text-slate-500">Common tasks</p>
                </div>
              </div>

              <div className="space-y-2">
                <ActionLink to="/builder" icon={Plus} label="Create Quote" color="blue" />
                <ActionLink to="/pricelist" icon={DollarSign} label="Update Pricelist" color="emerald" />
                <ActionLink to="/tier-rates" icon={Target} label="Tier Rates" color="amber" />
                <ActionLink to="/quotes" icon={FileText} label="All Quotes" color="slate" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  title,
  value,
  change,
  icon: Icon,
  color,
  isLoading,
  clickable = false,
}: {
  title: string
  value: string
  change: string
  icon: React.ElementType
  color: 'blue' | 'amber' | 'emerald' | 'violet'
  isLoading: boolean
  clickable?: boolean
}) {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    emerald: 'from-emerald-500 to-emerald-600',
    violet: 'from-violet-500 to-violet-600',
  }

  const bgClasses = {
    blue: 'bg-blue-50 dark:bg-blue-950/30',
    amber: 'bg-amber-50 dark:bg-amber-950/30',
    emerald: 'bg-emerald-50 dark:bg-emerald-950/30',
    violet: 'bg-violet-50 dark:bg-violet-950/30',
  }

  const hoverClasses = clickable ? {
    blue: 'hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer transition-all',
    amber: 'hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:border-amber-300 dark:hover:border-amber-700 cursor-pointer transition-all',
    emerald: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-300 dark:hover:border-emerald-700 cursor-pointer transition-all',
    violet: 'hover:bg-violet-100 dark:hover:bg-violet-900/40 hover:border-violet-300 dark:hover:border-violet-700 cursor-pointer transition-all',
  } : {}

  return (
    <div className={cn(
      'rounded-2xl p-5 border border-slate-200/60 dark:border-slate-800',
      bgClasses[color],
      clickable && hoverClasses[color]
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</p>
          {isLoading ? (
            <div className="h-9 w-16 bg-slate-200 dark:bg-slate-700 rounded mt-2 animate-pulse" />
          ) : (
            <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">{value}</p>
          )}
          {change && !isLoading && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{change}</p>
          )}
        </div>
        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-sm', colorClasses[color])}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  )
}

function MetricRow({
  label,
  value,
  isLoading,
  highlight = false,
  color,
}: {
  label: string
  value: string
  isLoading: boolean
  highlight?: boolean
  color?: 'emerald' | 'amber'
}) {
  const valueColor = color === 'emerald' 
    ? 'text-emerald-600 dark:text-emerald-400' 
    : color === 'amber'
    ? 'text-amber-600 dark:text-amber-400'
    : highlight
    ? 'text-blue-600 dark:text-blue-400'
    : 'text-slate-900 dark:text-white'

  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      {isLoading ? (
        <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      ) : (
        <span className={cn('font-semibold tabular-nums', valueColor)}>{value}</span>
      )}
    </div>
  )
}

function ActionLink({
  to,
  icon: Icon,
  label,
  color,
}: {
  to: string
  icon: React.ElementType
  label: string
  color: 'blue' | 'emerald' | 'amber' | 'slate'
}) {
  const iconColors = {
    blue: 'text-blue-600 dark:text-blue-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    slate: 'text-slate-600 dark:text-slate-400',
  }

  return (
    <Link
      to={to}
      className="group flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
    >
      <Icon className={cn('h-5 w-5', iconColors[color])} />
      <span className="font-medium text-slate-900 dark:text-white flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
    </Link>
  )
}
